// PostHog product-analytics integration.
//
// Wired into the existing analytics choke point (ga4.js): every meaningful,
// PII-scrubbed event the app already fires is forwarded here, plus login-time
// identify / logout-time reset. This gives the visit->purchase funnel, product
// usage, and session replay without re-instrumenting any call sites.
//
// The project token is a PostHog WRITE-ONLY public key — safe to ship in the
// client bundle (it can send events, never read data). Overridable via env.

import { POSTHOG_PERSONAL_DATA_QUERY_KEYS } from './analyticsPrivacy';
import { preparePostHogCapture, scrubPostHogEvent } from './posthogSchema';

const POSTHOG_KEY = String(
  process.env.REACT_APP_POSTHOG_KEY || 'phc_ngrmMJ8W8chkfSPHR2iVUxXQoSivcypZc6bGnUjw2soS'
).trim();
const POSTHOG_HOST = String(
  process.env.REACT_APP_POSTHOG_HOST || 'https://us.i.posthog.com'
).trim();

const MAX_PENDING_OPERATIONS = 100;
const MAX_LOAD_ATTEMPTS = 2;
const LOAD_RETRY_DELAY_MS = 500;
// Keep IP exemptions separate from both the SDK's legacy token-derived consent
// key and CASADATA's real cd_analytics_opt_out user/privacy gate. The marker is
// written only by posthogOptOut(), whose contract is the server-owned IP
// exemption, so a later allowed page may safely remove only this SDK decision.
const POSTHOG_CONSENT_PERSISTENCE_NAME = 'cd_posthog_consent_v2';
const POSTHOG_IP_EXEMPTION_MARKER = 'cd_posthog_ip_exemption_opt_out_v2';
const REPLAY_PRIVATE_CONTAINER_SELECTOR = [
  '[data-ph-mask]',
  '.accountPage',
  '.cdcoOverlay',
  '.upgradePage',
  '.apOverlay',
  '.statsArea',
  '.cdrd-overlay',
  '.suggestionsList',
  '.leaflet-popup-content',
  '.leaflet-tooltip',
  '.popupOuter'
].join(', ');
const REPLAY_SENSITIVE_TEXT_PATTERNS = [
  /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi,
  /(?:\+?\d[\d\s\-().]{8,}\d)/g,
  /\b(?:\d[ -]*?){13,19}\b/g,
  /\beyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\b/g,
  /\b\d{7,}\b/g
];
const BASE_CONTEXT = {
  analytics_schema_version: 2,
  app_name: 'casadata',
  app_surface: 'web',
  app_environment: String(
    process.env.REACT_APP_APP_ENV || (process.env.NODE_ENV === 'production' ? 'production' : 'development')
  ).slice(0, 32),
  auth_state: 'anonymous',
  plan_tier: 'anonymous',
  trial_user: false,
  ...(process.env.REACT_APP_VERSION
    ? { app_version: String(process.env.REACT_APP_VERSION).slice(0, 64) }
    : {})
};

let posthogClient = null;
let ready = false;
let loadPromise = null;
let loadAttempts = 0;
let retryTimer = null;
let optOutRequested = false;
let currentContext = { ...BASE_CONTEXT };
const pendingOperations = [];

const hasWindow = () => typeof window !== 'undefined';

const maskReplayText = (text, element) => {
  const value = String(text == null ? '' : text);
  try {
    if (
      element &&
      typeof element.closest === 'function' &&
      element.closest(REPLAY_PRIVATE_CONTAINER_SELECTOR)
    ) {
      return '*'.repeat(value.length);
    }
  } catch (e) {}

  return REPLAY_SENSITIVE_TEXT_PATTERNS.reduce(
    (safeText, pattern) => safeText.replace(pattern, (match) => '*'.repeat(match.length)),
    value
  );
};

const readLocalStorage = (key) => {
  if (!hasWindow()) return null;
  try {
    return window.localStorage.getItem(key);
  } catch (e) {
    return null;
  }
};

const writeLocalStorage = (key, value) => {
  if (!hasWindow()) return;
  try {
    window.localStorage.setItem(key, value);
  } catch (e) {}
};

const removeLocalStorage = (key) => {
  if (!hasWindow()) return;
  try {
    window.localStorage.removeItem(key);
  } catch (e) {}
};

const recoverExemptionOwnedConsent = () => {
  if (readLocalStorage(POSTHOG_IP_EXEMPTION_MARKER) !== '1') return;
  // This runs only after ga4.js has independently allowed analytics, including
  // DNT/GPC and cd_analytics_opt_out checks. Removing the exact versioned SDK
  // key before init avoids a synthetic $opt_in event and lets replay start as a
  // normal unconsented-by-SDK (but app-permitted) visit.
  removeLocalStorage(POSTHOG_CONSENT_PERSISTENCE_NAME);
  removeLocalStorage(POSTHOG_IP_EXEMPTION_MARKER);
};

const applyClientOptOut = () => {
  if (!posthogClient) return;
  try {
    if (typeof posthogClient.stopSessionRecording === 'function') {
      posthogClient.stopSessionRecording();
    }
    if (typeof posthogClient.opt_out_capturing === 'function') {
      posthogClient.opt_out_capturing();
    }
  } catch (e) {}
};

const runOperation = ({ method, args }) => {
  if (!ready || !posthogClient || optOutRequested) return;
  try {
    const operation = posthogClient[method];
    if (typeof operation === 'function') {
      operation.apply(posthogClient, args);
    }
  } catch (e) {
    // Analytics must never break the app.
  }
};

const flushPendingOperations = () => {
  if (!ready || !posthogClient || optOutRequested) return;
  const operations = pendingOperations.splice(0, pendingOperations.length);
  operations.forEach(runOperation);
};

const enqueueOrRun = (method, args) => {
  if (!hasWindow() || optOutRequested) return;
  if (ready && posthogClient) {
    runOperation({ method, args });
    return;
  }

  if (pendingOperations.length >= MAX_PENDING_OPERATIONS) {
    pendingOperations.shift();
  }
  pendingOperations.push({ method, args });
};

export const initPostHog = () => {
  // Caller (ga4.initializeAnalytics) has already passed shouldTrack(), so this
  // only runs when analytics is permitted for this visitor/host.
  if (
    !hasWindow() ||
    ready ||
    loadPromise ||
    loadAttempts >= MAX_LOAD_ATTEMPTS ||
    optOutRequested ||
    !POSTHOG_KEY
  ) return loadPromise;

  try {
    if (retryTimer !== null) {
      window.clearTimeout(retryTimer);
      retryTimer = null;
    }
    recoverExemptionOwnedConsent();
    loadAttempts += 1;
    loadPromise = import('posthog-js')
      .then((module) => {
        // An exemption verdict can arrive while the chunk is in flight. In
        // that case the code may already be downloaded, but it must never be
        // initialized or send anything.
        if (optOutRequested) return null;

        const loadedClient = module?.default || module;
        if (!loadedClient || typeof loadedClient.init !== 'function') {
          throw new Error('posthog_client_unavailable');
        }
        posthogClient = loadedClient;
        posthogClient.init(POSTHOG_KEY, {
          api_host: POSTHOG_HOST,
          defaults: '2026-06-25',
          // Versioning isolates this app-owned consent state from the legacy
          // __ph_opt_in_out_<token> key that IP exemptions could poison.
          consent_persistence_name: POSTHOG_CONSENT_PERSISTENCE_NAME,
          opt_out_capturing_persistence_type: 'localStorage',
          // Only build person profiles for logged-in users we identify — keeps
          // anonymous browsing lightweight and privacy-friendly.
          person_profiles: 'identified_only',
          // App state owns virtual navigation. ga4.js sends one logical
          // page_view and the destination adapter translates it to $pageview.
          capture_pageview: false,
          capture_pageleave: true,
          autocapture: {
            dom_event_allowlist: ['click', 'submit'],
            element_allowlist: ['a', 'button', 'form'],
            css_selector_ignorelist: [
              '.ph-no-autocapture',
              '[data-ph-no-autocapture]',
              '.ph-no-capture',
              '[data-ph-no-capture]',
              '.leaflet-container',
              '.leaflet-control',
              '.leaflet-popup'
            ],
            element_attribute_ignorelist: ['href', 'value', 'title', 'aria-label'],
            capture_copied_text: false
          },
          rageclick: {
            css_selector_ignorelist: [
              '.ph-no-capture',
              '[data-ph-no-capture]',
              '.ph-no-rageclick',
              '.leaflet-container',
              '.leaflet-control',
              '[role="slider"]'
            ],
            content_ignorelist: true,
            ignore_text_selection: true
          },
          // Error Tracking: auto-capture uncaught errors + unhandled promise
          // rejections as proper $exception events (grouped issues + stack traces
          // in PostHog), with PII scrubbed on the way out.
          capture_exceptions: true,
          capture_performance: {
            network_timing: false,
            web_vitals: true,
            web_vitals_attribution: false
          },
          enable_recording_console_log: false,
          respect_dnt: true,
          disable_capture_url_hashes: true,
          mask_personal_data_properties: true,
          custom_personal_data_properties: POSTHOG_PERSONAL_DATA_QUERY_KEYS,
          mask_all_element_attributes: true,
          property_denylist: [
            // `token` is intentionally absent: posthog-js adds its public
            // project token to every event immediately before delivery.
            'email', 'phone', 'address', 'password', 'authorization',
            'checkoutAuth', 'verifyEmail', 'trialToken', 'fingerprint',
            'buyer_name', 'seller_name', 'owner_name', 'contact_name',
            'latitude', 'longitude', 'coordinates', 'prefix', 'cadastral_id',
            'comment', 'comments', 'free_text', 'feedback_text', 'cancellation_comment'
          ],
          before_send: scrubPostHogEvent,
          session_recording: {
            // Form values remain fully masked: they can contain passwords,
            // contact details, addresses, searches, and payment-adjacent data.
            maskAllInputs: true,
            // Pass every non-input text node through a local privacy function:
            // ordinary UI copy is returned unchanged, while private surfaces
            // and recognizable personal identifiers are redacted in-browser.
            maskTextSelector: '*',
            maskTextFn: maskReplayText,
            // Free-form conversations are blocked so prompts and generated
            // answers never enter replay payloads at all.
            blockSelector: '[data-ph-block], .chatbot-container, .cd-chat-root',
            recordHeaders: false,
            recordBody: false,
            recordCrossOriginIframes: false,
            collectFonts: false,
          },
          loaded: (client) => {
            if (client && typeof client.capture === 'function') {
              posthogClient = client;
            }
            if (optOutRequested) {
              ready = false;
              applyClientOptOut();
              return;
            }
            try {
              if (client && typeof client.register === 'function') {
                client.register(currentContext);
              }
            } catch (e) {}
            ready = true;
            flushPendingOperations();
          },
        });

        if (optOutRequested) {
          applyClientOptOut();
          posthogClient = null;
          return null;
        }

        // posthog.init accepts events immediately; do not wait on optional
        // remote configuration before releasing the bounded local queue.
        try {
          if (typeof posthogClient.register === 'function') {
            posthogClient.register(currentContext);
          }
        } catch (e) {}
        ready = true;
        flushPendingOperations();
        return posthogClient;
      })
      .catch(() => {
        ready = false;
        posthogClient = null;
        loadPromise = null;
        // A transient lazy-chunk failure should not permanently discard the
        // capture queue. Retry once, and cancel that retry if an exemption
        // arrives in the meantime.
        if (!optOutRequested && loadAttempts < MAX_LOAD_ATTEMPTS && hasWindow()) {
          retryTimer = window.setTimeout(() => {
            retryTimer = null;
            if (!optOutRequested) initPostHog();
          }, LOAD_RETRY_DELAY_MS);
        }
        return null;
      });
  } catch (e) {
    // Analytics must never break the app.
    ready = false;
    posthogClient = null;
    loadPromise = null;
    if (!optOutRequested && loadAttempts < MAX_LOAD_ATTEMPTS && hasWindow()) {
      retryTimer = window.setTimeout(() => {
        retryTimer = null;
        if (!optOutRequested) initPostHog();
      }, LOAD_RETRY_DELAY_MS);
    }
  }

  return loadPromise;
};

export const posthogCapture = (event, properties) => {
  try {
    const prepared = preparePostHogCapture(event, properties);
    if (!prepared) return;
    enqueueOrRun('capture', [prepared.event, prepared.properties]);
  } catch (e) {}
};

// Tie behavior to a real account. Called on login with the numeric account id
// (never email) so the funnel and session replays attribute to the same person
// the server-side purchase events use.
export const posthogIdentify = (userId, properties) => {
  try {
    if (!hasWindow() || optOutRequested) return;
    const id = String(userId || '').trim();
    if (!id || id.includes('@')) return;
    currentContext = {
      ...BASE_CONTEXT,
      ...(properties || {}),
      auth_state: 'authenticated',
      trial_user: false
    };
    enqueueOrRun('identify', [id, currentContext]);
    enqueueOrRun('register', [currentContext]);
  } catch (e) {}
};

// Register bounded trial/anonymous context without creating a person profile or
// resetting the anonymous distinct ID. This preserves pre-signup attribution.
export const posthogSetContext = (properties = {}) => {
  try {
    currentContext = { ...BASE_CONTEXT, ...(properties || {}) };
    enqueueOrRun('register', [currentContext]);
  } catch (e) {}
};

export const posthogReset = () => {
  try {
    currentContext = { ...BASE_CONTEXT };
    enqueueOrRun('reset', []);
    enqueueOrRun('register', [currentContext]);
  } catch (e) {}
};

// React render errors are swallowed by the error boundary and never reach
// window.onerror, so autocapture misses them — capture them explicitly.
export const posthogCaptureException = (error, properties) => {
  try {
    const prepared = preparePostHogCapture('frontend_exception', properties || {});
    enqueueOrRun('captureException', [error, prepared?.properties || {}]);
  } catch (e) {}
};

// Hard stop for exempt visitors (owner IPs): kills autocapture/replay even if
// init already ran before the IP check answered. Safe no-op when never inited.
export const posthogOptOut = () => {
  if (!hasWindow()) return;
  optOutRequested = true;
  ready = false;
  writeLocalStorage(POSTHOG_IP_EXEMPTION_MARKER, '1');
  if (retryTimer !== null) {
    window.clearTimeout(retryTimer);
    retryTimer = null;
  }
  pendingOperations.splice(0, pendingOperations.length);
  applyClientOptOut();
};

const posthogAnalytics = {
  initPostHog,
  posthogCapture,
  posthogIdentify,
  posthogSetContext,
  posthogReset,
  posthogCaptureException,
  posthogOptOut,
};

export default posthogAnalytics;
